import nextSynergiesJson from "../content/next-synergies.json";
import tierFiveSixSynergiesJson from "../content/tier-five-six-synergies.json";
import type {
  SynergyDefinition,
  SynergyMechanicDefinition,
} from "./phase-three-types";

interface NextSynergyDocument {
  version: 1;
  synergies: SynergyDefinition[];
}

const document = nextSynergiesJson as NextSynergyDocument;
const tierFiveSixDocument =
  tierFiveSixSynergiesJson as NextSynergyDocument;

export const NEXT_SYNERGIES: SynergyDefinition[] = [
  ...document.synergies,
  ...tierFiveSixDocument.synergies,
].map(
  (synergy) => ({
    ...synergy,
    requirements: synergy.requirements.map((requirement) =>
      requirement.kind === "one_of"
        ? { ...requirement, moduleIds: [...requirement.moduleIds] }
        : { ...requirement },
    ),
    bonuses: { ...synergy.bonuses },
    abilityModifiers: synergy.abilityModifiers?.map((modifier) => ({
      ...modifier,
      moduleIds: [...modifier.moduleIds],
    })),
    mechanics: synergy.mechanics?.map((mechanic) =>
      cloneSynergyMechanic(mechanic),
    ),
  }),
);

function cloneSynergyMechanic(
  mechanic: SynergyMechanicDefinition,
): SynergyMechanicDefinition {
  if (mechanic.kind === "hidden_effect_amplifier") {
    return { ...mechanic, hiddenEffectIds: [...mechanic.hiddenEffectIds] };
  }
  if (mechanic.kind === "undead_catastrophe") {
    return {
      ...mechanic,
      normalPool: mechanic.normalPool.map((entry) => ({ ...entry })),
      heavyPool: mechanic.heavyPool.map((entry) => ({ ...entry })),
    };
  }
  if (mechanic.kind === "divine_guard_upgrade") {
    return {
      ...mechanic,
      suppressedModuleIds: [...mechanic.suppressedModuleIds],
    };
  }
  if (mechanic.kind === "surface_mining_skill_amplifier") {
    return {
      ...mechanic,
      excludedModuleIds: [...mechanic.excludedModuleIds],
    };
  }
  if (
    mechanic.kind === "paper_units" ||
    mechanic.kind === "distant_target_bonus" ||
    mechanic.kind === "thunderstorm_counter" ||
    mechanic.kind === "freeze_combo" ||
    mechanic.kind === "hot_floor" ||
    mechanic.kind === "guard_council" ||
    mechanic.kind === "nest_riot" ||
    mechanic.kind === "dual_extremes_nth" ||
    mechanic.kind === "echo_transfer" ||
    mechanic.kind === "stacking_armor_break" ||
    mechanic.kind === "guard_coordination" ||
    mechanic.kind === "summon_death_cooldown" ||
    mechanic.kind === "follower_trio_combo" ||
    mechanic.kind === "stored_experience_companion_damage"
  ) {
    return { ...mechanic, abilityIds: [...mechanic.abilityIds] };
  }
  if (mechanic.kind === "needle_heal_counter") {
    return { ...mechanic, abilityIds: [...mechanic.abilityIds] };
  }
  if (mechanic.kind === "bitter_medicine") {
    return {
      ...mechanic,
      healingAbilityIds: [...mechanic.healingAbilityIds],
    };
  }
  if (mechanic.kind === "suppress_modules") {
    return { ...mechanic, moduleIds: [...mechanic.moduleIds] };
  }
  if (
    mechanic.kind === "long_range_strike" ||
    mechanic.kind === "thunder_god_indulgence"
  ) {
    return { ...mechanic, abilityIds: [...mechanic.abilityIds] };
  }
  if (mechanic.kind === "absolute_domain") {
    return { ...mechanic, sourceAbilityIds: [...mechanic.sourceAbilityIds] };
  }
  if (mechanic.kind === "fire_land") {
    return { ...mechanic, sourceAbilityIds: [...mechanic.sourceAbilityIds] };
  }
  if (mechanic.kind === "the_world") {
    return { ...mechanic, sourceAbilityIds: [...mechanic.sourceAbilityIds] };
  }
  if (mechanic.kind === "greed_and_wealth") {
    return {
      ...mechanic,
      suppressedModuleIds: [...mechanic.suppressedModuleIds],
    };
  }
  if (mechanic.kind === "dual_gods_agreement") {
    return {
      ...mechanic,
      contractAbilityIds: [...mechanic.contractAbilityIds],
      randomCastAbilityIds: [...mechanic.randomCastAbilityIds],
    };
  }
  return { ...mechanic };
}
